/**
 * Purpose: instruct LLMs how to read Swedish building drawings or CAD source
 *          text so Nodon can extrude a footprint and storey stack.
 *
 * Input:   file attachment (typically PDF) or inlined CAD source text (DXF/IFC),
 *          filename context, optional caller instructions.
 *          Language: Swedish construction documents.
 * Output:  structured output; shape defined by the caller's JSON schema
 *          (`pdf_geometry_import` for standalone design-AI import, or
 *          blueprint batch `buildings[]` in
 *          {@link buildBlueprintExtractionRootJsonSchema}).
 * Consumers:
 *   - `server/src/controllers/design-ai.controller.ts` (standalone plan import)
 *   - `server/src/services/project-file-batches/project-file-segment-extraction.ts`
 *     (batch blueprint extractor)
 *   - Import geometry dialog Advanced system prompt (admins may edit the text)
 *
 * Schema is the source of truth for field shapes and nullability — this prompt
 * only encodes *how to read* the drawing and how Nodon uses the result. Arity
 * (one building vs many) is a caller concern and must be passed in via `arity`.
 */

import { assertUnreachable } from './function.helpers';
import type { GeometryImportCadKind } from './design/geometry-import-capabilities.helpers';

export type GeometryImportPromptSourceForm = 'attachment' | 'cad-source-text';

export type BuildPdfGeometryPromptInput = {
  projectName?: string;
  /**
   * `single` — standalone design-AI import: return one dominant building.
   * `multi`  — batch blueprint extractor: emit every distinct building visible
   *            in the segment (caller supplements via `extraInstructions`).
   */
  arity: 'single' | 'multi';
  /** Optional caller instructions appended verbatim. */
  extraInstructions?: string;
  /**
   * How the drawing reaches the model. `attachment` — the document itself is
   * uploaded. `cad-source-text` — geometry entities were extracted from an
   * ASCII CAD export and inlined as text. Defaults to `attachment`.
   */
  sourceForm?: GeometryImportPromptSourceForm;
  /**
   * When {@link BuildPdfGeometryPromptInput.sourceForm} is `cad-source-text`,
   * selects DXF vs IFC reading rules. Ignored for PDF attachments.
   */
  cadKind?: GeometryImportCadKind;
};

/**
 * System instructions for standalone design-AI geometry import.
 * Replaces the chat/image persona so the model does not wrap JSON in prose.
 */
export const PDF_GEOMETRY_IMPORT_SYSTEM_INSTRUCTIONS: string[] = [
  'Task: reconstruct THIS building for Nodon design import — a ground-floor heated-envelope footprint (no balconies) that will be extruded, plus a storey stack.',
  'A plan outline beats a section cut. Follow the response schema.',
];

/**
 * Numbered drawing rules shared by PDF import and CAD source text.
 * Models follow numbered constraints better than a single prose sentence.
 */
export const BUILDING_GEOMETRY_IMPORT_READING_RULES = [
  'Drawing-reading rules (apply before guessing):',
  '1. Prefer a plan (SV: planritning) for the footprint. Trace the outer wall of the heated volume. Ignore furniture, hatching, grids, dimension strings, and the titleblock.',
  '2. Keep jogs, courtyards, and L/U/atrium plans. Do not replace an irregular outline with its bounding box or convex hull.',
  '3. A section or elevation (SV: sektion, fasad) is a vertical cut — not a plan. Do not use the cut outline as the footprint. Infer a rectangle from ONE cut’s ground-line width × a conservative typical depth. Several similar cuts on one sheet are views of the same building.',
  '4. Filename letters in Swedish CAD sets: P = plan, S = sektion, F = fasad.',
  '5. Read storeys from stacked floor labels, FG/BV/plan notes, section floor bands, and CAD TEXT records (`Name +elevationMeters`). Count every storey the drawing shows. A storey in the roof counts. Do not drop a storey because it is not occupiable. Do not count the roof surface or the eaves (SV: takyta, takfot) and do not count titleblock rows.',
  '6. Nodon extrudes one footprint through all storeys. If the drawing shows setbacks or a tower on a podium, use the ground-floor heated outline (the largest mass on grade, excluding balconies).',
  '7. Units are meters. Convert mm/cm on dimensioned PDF drawings. CAD source text is already world-space — never apply a paper scale such as 1:40 to those coordinates.',
  '8. Exclude balconies, terraces, access galleries, and canopies (SV: balkong, altan, loftgång, skärmtak). Do not union those protrusions into the footprint.',
].join('\n');

/** How the attached PDF/drawing should be scaled. */
export const ATTACHMENT_DRAWING_LEGEND = [
  'How to read the attached document:',
  '- Search for paper size (A4/A3/A2/A1) and scale notation (1:400, 1:200, 1:100) and convert plan dimensions to meters.',
  '- If only a scale bar or dimensioned extents are present, infer from those. Otherwise use common Swedish plan conventions.',
].join('\n');

/**
 * Field-by-field instructions for inlined CAD geometry text.
 * Mirrors the DXF compact records produced by dxf-geometry-text helpers.
 */
export const CAD_SOURCE_TEXT_LEGEND = [
  'How to read the CAD source text (this is the drawing, already exploded):',
  '- Header line states units (meters from DXF INSUNITS when known), origin shifted to the bounding-box min corner, and axes +x east +y south (Nodon map).',
  '- LINE x1 y1 x2 y2 — a segment.',
  '- POLY x,y x,y … CLOSED — a polyline loop.',
  '- CIRCLE cx cy r / ARC cx cy r startDeg endDeg.',
  '- TEXT x y label — Swedish labels (FG +12.40, BV, Plan 3) are storey evidence.',
  '- DXF INSERT/BLOCKS are already exploded to world coordinates. Paper-space titleblocks are omitted when model-space geometry exists.',
  '- Do not apply a paper scale (1:100, 1:40). Coordinates are meters.',
].join('\n');

/**
 * IFC-specific reading rules. Floor slabs become POLY records; IfcBuildingStorey
 * becomes TEXT `Name +elevationMeters`. Balcony slabs are often extra POLYs.
 */
export const IFC_SOURCE_TEXT_LEGEND = [
  'How to read the IFC source text (exploded world-space slabs and storeys):',
  '- Header line states units (meters from IFCSIUNIT), origin shifted to the bounding-box min corner, and axes +x east +y south (Nodon map).',
  '- POLY x,y x,y … CLOSED — a floor-slab (or wall) outline in XY. Several POLYs are stacked storeys plus extras, not one unioned building.',
  '- LINE / CIRCLE / ARC — wall or profile fragments. Use them only if no closed POLY describes the heated floor plate.',
  '- TEXT x y label — IfcBuildingStorey evidence. Labels look like `Plan 1 +0`, `BV +0`, or `Våning 3 +6.4`: a name, then finished-floor elevation in meters (the +number). TEXT x y is plan position, not height.',
  '- Coordinates are already meters. Do not apply a paper scale.',
  '',
  'Footprint — heated envelope, no balconies:',
  '1. Pick ONE closed POLY: the repeating floor plate of the heated volume (SV: uppvärmd area / klimatskärm). That is the outer-wall outline Nodon will extrude.',
  '2. Do not union every POLY. Unioning pulls in balcony slabs, landings, canopies, and access galleries (SV: balkong, loftgång, altan, skärmtak).',
  '3. Drop a protrusion that is a smaller rectangle sharing one edge with the main plate, or a POLY that appears on some floors only. Keep courtyards and L/U/atrium jogs of the heated plate.',
  '4. If several similar large POLYs exist (one per storey), use the ground-floor plate — the one whose Z is implied by the lowest occupiable TEXT elevation. Do not use the roof slab or the widest balcony-inclusive outline.',
  '5. Do not replace the plate with a bounding box or convex hull.',
  '',
  'Storeys — from TEXT elevations:',
  '1. Collect IfcBuildingStorey TEXT labels and sort them by the +elevation number, lowest first. That ordered list is the storey stack. Count every storey the model shows, including one in the roof.',
  '2. Skip a TEXT that is the roof surface or the eaves (SV: takyta, takfot), or a site/terrain level with no floor plate. A storey named takplan counts. Keep källare/souterräng when it is a full storey in the model.',
  '3. Floor-to-floor heights are the differences between consecutive storey elevations. Repeat equal heights; do not emit unique-only values or the raw +elevations.',
  '4. n storey TEXT elevations give n-1 known deltas. For the top storey, repeat the typical delta of the stack unless a roof/parapet TEXT remains as a height datum (then last height = roofElev - lastFloorElev).',
  '5. buildingHeightMeters should match the sum of those floor-to-floor heights (ground to roof/parapet).',
].join('\n');

const TASK_BY_ARITY: Record<BuildPdfGeometryPromptInput['arity'], string> = {
  single:
    'Task: reconstruct THIS building so Nodon can extrude it — one ground-floor heated-envelope polygon (no balconies) and a storey stack. Not a sheet description, not a generic box.',
  multi:
    'Task: describe every distinct physical building visible in this segment for downstream design modelling.',
};

const CONSUMER_NOTES_BY_ARITY: Record<
  BuildPdfGeometryPromptInput['arity'],
  string
> = {
  single: [
    'How Nodon uses the result:',
    '- points2D is the plan extruded on every storey.',
    '- storeyHeightsMeters becomes each storey’s floor-to-floor height, in order. Repeat equal heights; do not emit unique-only values or cumulative FG elevations.',
    '- If storeyHeightsMeters is omitted, storeyCount splits buildingHeightMeters into equal floors.',
    '- Extra prose or fields are discarded.',
  ].join('\n'),
  multi: [
    'How to fill the schema:',
    '- One row per distinct PHYSICAL building. Plan, section, and elevation of the same building collapse into one row; merge their evidence.',
    '- Numeric fields are what Nodon can model directly. If the schema exposes `accurate_description`, put quantitative massing facts there (setbacks, podium + tower, extra wings) even when they do not fit the structured fields.',
  ].join('\n'),
};

const ARITY_BUILDING_RULE: Record<
  BuildPdfGeometryPromptInput['arity'],
  string
> = {
  single:
    'If several physical buildings are shown, pick the largest or most prominent. Plan + section + elevation of the same building is one result.',
  multi:
    "Sites with several houses need several rows. A name for a piece of one connected volume is `dimensions.parts` on that building's row, not a new row. Do not emit separate rows for different views of the same building.",
};

const SWEDISH_CONTEXT =
  'Swedish terms: planritning = plan, sektion = section, fasad = elevation, BV = ground floor, FG = finished floor, balkong = balcony, loftgång = access gallery, tak = roof. Boverket/BBR may inform reasoning, not the numeric output.';

/**
 * Appends optional caller instructions after the built prompt body.
 * Used by {@link buildPdfGeometryPrompt} and when an admin-edited system prompt
 * is sent from the Import geometry dialog.
 */
export const appendGeometryImportExtraInstructions = (
  prompt: string,
  extraInstructions?: string,
): string => {
  const extra = extraInstructions?.trim();
  if (!extra) return prompt;
  return `${prompt}\n\nAdditional instructions from the user:\n${extra}`;
};

/** Inputs for {@link resolveGeometryImportPrompt}. */
export type ResolveGeometryImportPromptInput = BuildPdfGeometryPromptInput & {
  /** Admin-edited drawing-reading prompt. When set, replaces the default body. */
  systemPrompt?: string;
};

/**
 * Resolve the user-message prompt for standalone geometry import.
 * Blank `systemPrompt` values fall back to {@link buildPdfGeometryPrompt}.
 */
export const resolveGeometryImportPrompt = (
  input: ResolveGeometryImportPromptInput,
): string => {
  const clientPrompt = input.systemPrompt?.trim();
  if (clientPrompt) {
    return appendGeometryImportExtraInstructions(
      clientPrompt,
      input.extraInstructions,
    );
  }
  return buildPdfGeometryPrompt(input);
};

/**
 * User-message geometry prompt. Admins may replace this text in the Import
 * geometry dialog; extra instructions are still appended last.
 */
export const buildPdfGeometryPrompt = (
  input: BuildPdfGeometryPromptInput,
): string => {
  const projectLabel = JSON.stringify(
    (input.projectName ?? 'site').replace(/\r?\n/g, ' '),
  );
  const sourceForm = input.sourceForm ?? 'attachment';
  const cadKind = input.cadKind;
  const base = [
    TASK_BY_ARITY[input.arity],
    '',
    sourceFormOpening(sourceForm, cadKind, projectLabel),
    sourceFormIntro(sourceForm, cadKind),
    '',
    BUILDING_GEOMETRY_IMPORT_READING_RULES,
    ARITY_BUILDING_RULE[input.arity],
    '',
    CONSUMER_NOTES_BY_ARITY[input.arity],
    '',
    sourceFormLegend(sourceForm, cadKind),
    '',
    SWEDISH_CONTEXT,
  ].join('\n');
  return appendGeometryImportExtraInstructions(base, input.extraInstructions);
};

const sourceFormOpening = (
  sourceForm: GeometryImportPromptSourceForm,
  cadKind: GeometryImportCadKind | undefined,
  projectLabel: string,
): string => {
  switch (sourceForm) {
    case 'attachment':
      return `You are extracting geometry from a Swedish building plan document (typically a PDF) for project ${projectLabel}.`;
    case 'cad-source-text':
      switch (cadKind) {
        case 'ifc':
          return `You are extracting geometry from exploded IFC outline text for project ${projectLabel}.`;
        case 'dxf':
          return `You are extracting geometry from exploded DXF outline text for project ${projectLabel}.`;
        case undefined:
          return `You are extracting geometry from exploded CAD outline text (DXF or IFC) for project ${projectLabel}.`;
        default:
          return assertUnreachable(cadKind);
      }
    default:
      return assertUnreachable(sourceForm);
  }
};

const sourceFormIntro = (
  sourceForm: GeometryImportPromptSourceForm,
  cadKind: GeometryImportCadKind | undefined,
): string => {
  switch (sourceForm) {
    case 'attachment':
      return 'The drawing is attached as a document.';
    case 'cad-source-text':
      switch (cadKind) {
        case 'ifc':
          return 'The drawing is provided below as compact IFC geometry text. Floor slabs are POLY records; IfcBuildingStorey names are TEXT records with +elevation in meters. Local placements are already exploded.';
        case 'dxf':
          return 'The drawing is provided below as compact DXF geometry text. INSERT/BLOCKS are exploded to world coordinates in meters; paper-space titleblocks are omitted when model-space geometry exists. TEXT labels (floor heights, room names) are kept.';
        case undefined:
          return 'The drawing is provided below as compact CAD geometry text. DXF INSERT/BLOCKS and IFC local placements are exploded to world coordinates in meters; paper-space titleblocks are omitted when model-space geometry exists. TEXT labels (floor heights, room names, IFC storeys) are kept.';
        default:
          return assertUnreachable(cadKind);
      }
    default:
      return assertUnreachable(sourceForm);
  }
};

const sourceFormLegend = (
  sourceForm: GeometryImportPromptSourceForm,
  cadKind: GeometryImportCadKind | undefined,
): string => {
  switch (sourceForm) {
    case 'attachment':
      return ATTACHMENT_DRAWING_LEGEND;
    case 'cad-source-text':
      switch (cadKind) {
        case 'ifc':
          return IFC_SOURCE_TEXT_LEGEND;
        case 'dxf':
        case undefined:
          return CAD_SOURCE_TEXT_LEGEND;
        default:
          return assertUnreachable(cadKind);
      }
    default:
      return assertUnreachable(sourceForm);
  }
};
